import React, {useEffect, useMemo, useState} from "react";
import {Tabs} from "antd";
import {useFetch} from "../../hooks/useFetch";
import {useStore} from "../../hooks/useStore";
import moment from "moment";
import {useHistory, useLocation} from "react-router-dom";
import {useObserver} from "mobx-react-lite";
import {ObjectionModal} from "./modals/ObjectionModal";
import CreditLogFilters from "./components/CreditLogFilters";
import {useCreditLog} from "./hooks/useCreditLog";
import ActionButtons from "./components/ActionButtons";
import {VFlex} from "components/_defaultComponents/Flex/VFlex";
import {CreditLogTableTab} from "./components/CreditLogTableTab";
import {CourierDetailTotal} from "./courierDetailTotal/CourierDetailTotal";
import {SubcompanyDetailTotal} from "./subcompanyDetailTotal/SubcompanyDetailTotal";
import {
    buildPaymentTransactionLogFilterParams,
    normalizeCompanyIdForApi,
    normalizeSubCompanyIdForApi
} from "./buildPaymentTransactionLogListParams";

export const CreditLogContext = React.createContext();
export const CreditLogTable = () => {
    const stores = useStore();
    const history = useHistory();
    const location = useLocation();

    const getUrlParameter = name => {
        const params = new URLSearchParams(location.search);
        return params.get(name);
    };

    const periodIdFromUrl = getUrlParameter("periodId");
    const tabFromUrl = getUrlParameter("tab") || "creditLogTable";
    // Carili Firma Faturaları'ndaki detay butonu, faturanın dönemine ait hareketleri açar
    const invoiceIdFromUrl = getUrlParameter("invoiceId");
    const [selectedTransactionType, setSelectedTransactionType] = useState([]);
    const [selectedSubType, setSelectedSubType] = useState([]);
    const [selectedHasWalletTransaction, setSelectedHasWalletTransaction] = useState("all");
    const startDateFromUrl = moment(getUrlParameter("startDate"), "YYYY-MM-DD", true);
    const endDateFromUrl = moment(getUrlParameter("endDate"), "YYYY-MM-DD", true);
    const defaultDateRange =
        startDateFromUrl.isValid() && endDateFromUrl.isValid()
            ? [startDateFromUrl, endDateFromUrl]
            : [moment().subtract(0, "days"), moment()];
    const [selectedDateRange, setSelectedDateRange] = useState(defaultDateRange);
    const [isDateRangeChecked, setIsDateRangeChecked] = useState(!periodIdFromUrl);
    const [selectedPeriod, setSelectedPeriod] = useState(periodIdFromUrl || null);
    const [isPeriodChecked, setIsPeriodChecked] = useState(Boolean(periodIdFromUrl));
    const [companyId, setSelectedCompanyId] = useState(undefined);
    const [subCompanyId, setSelectedSubCompanyId] = useState(undefined);
    const [activeTab, setActiveTab] = useState(tabFromUrl);
    const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState("all");
    const [selectedProviderCompany, setSelectedProviderCompany] = useState("all");
    const [selectedCreditMode, setSelectedCreditMode] = useState([]);
    const handleTabChange = key => {
        setActiveTab(key);
        const params = new URLSearchParams(location.search);
        params.set("tab", key);
        history.replace({search: params.toString()});
    };

    // Dönem seçildiğinde tarihleri temizle
    const handlePeriodChange = period => {
        setSelectedPeriod(period);
        if (period) {
            setSelectedDateRange(null);
        }
    };
    const [pageParam, setPageParam] = useState(1);
    const [loadingExcelData, setLoadingExcelData] = useState(false);
    const [loadingExcelDataV2, setLoadingExcelDataV2] = useState(false);
    const [updateFileUpdateVisibilty, setFileUpdateVisibility] = useState(false);
    const [orderId, setOrderId] = useState(null);
    const [deliveryId, setDeliveryId] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [subcompanyIdForObjection, setSubcompanyIdForObjection] = useState(null);
    const [workType, setSelectedWorkType] = useState("all");
    const [searchText, setSearchText] = useState("");

    const resolvedCompanyId = normalizeCompanyIdForApi(companyId);
    const resolvedSubCompanyId = normalizeSubCompanyIdForApi(subCompanyId);
    const sharedLogFilters = buildPaymentTransactionLogFilterParams({
        companyId: resolvedCompanyId,
        selectedDateRange,
        isDateRangeChecked,
        isPeriodChecked,
        selectedPeriod,
        selectedTransactionType,
        selectedSubType,
        selectedHasWalletTransaction,
        workType,
        searchText,
        selectedInvoiceStatus,
        selectedProviderCompany,
        selectedCreditMode,
        activeTab,
        invoiceId: invoiceIdFromUrl
    });

    useEffect(() => {
        setPageParam(1);
    }, [
        companyId,
        subCompanyId,
        selectedTransactionType,
        selectedSubType,
        selectedHasWalletTransaction,
        selectedDateRange,
        workType,
        searchText,
        isDateRangeChecked,
        isPeriodChecked,
        selectedPeriod,
        activeTab,
        selectedInvoiceStatus,
        selectedProviderCompany,
        selectedCreditMode
    ]);
    const creditLogContext = useCreditLog(
        companyId,
        subCompanyId,
        selectedDateRange,
        isDateRangeChecked,
        isPeriodChecked,
        selectedPeriod,
        selectedTransactionType,
        selectedSubType,
        selectedHasWalletTransaction,
        workType,
        searchText,
        selectedInvoiceStatus,
        activeTab,
        setLoadingExcelData,
        setLoadingExcelDataV2,
        selectedProviderCompany,
        selectedCreditMode
    );
    const {handleDownloadExcel, handleDownloadExcelV2} = creditLogContext;

    const listQueryKey = useMemo(
        () =>
            [
                pageParam,
                workType,
                companyId,
                subCompanyId,
                isDateRangeChecked,
                isPeriodChecked,
                selectedPeriod,
                selectedTransactionType.join(","),
                selectedSubType.join(","),
                selectedHasWalletTransaction,
                selectedInvoiceStatus,
                selectedProviderCompany,
                selectedCreditMode?.join(",") ?? "",
                searchText,
                activeTab,
                selectedDateRange?.[0]?.valueOf?.() ?? "",
                selectedDateRange?.[1]?.valueOf?.() ?? ""
            ].join("|"),
        [
            pageParam,
            workType,
            companyId,
            subCompanyId,
            isDateRangeChecked,
            isPeriodChecked,
            selectedPeriod,
            selectedTransactionType,
            selectedSubType,
            selectedHasWalletTransaction,
            selectedInvoiceStatus,
            selectedProviderCompany,
            selectedCreditMode,
            searchText,
            activeTab,
            selectedDateRange
        ]
    );

    const [paymentTransactionLogsLoading, paymentTransactionLogs, _, __, refetch] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyPaymentTransactionLogList.bind(null, {
            ...(resolvedSubCompanyId ? {subCompanyId: resolvedSubCompanyId} : {}),
            skip: pageParam - 1,
            limit: 10,
            ...sharedLogFilters
        }),
        initialCall: false,
        showLoading: true,
        dependencies: [
            companyId,
            subCompanyId,
            selectedTransactionType,
            selectedSubType,
            selectedHasWalletTransaction,
            selectedDateRange,
            pageParam,
            workType,
            searchText,
            isDateRangeChecked,
            isPeriodChecked,
            selectedPeriod,
            activeTab,
            selectedInvoiceStatus,
            selectedProviderCompany,
            selectedCreditMode
        ]
    });

    const [paymentTransactionsLogCalculationLoading, paymentTransactionsLogCalculation] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.paymentTransactionsLogCalculation.bind(null, {
            ...(resolvedSubCompanyId ? {subCompanyId: resolvedSubCompanyId} : {}),
            ...sharedLogFilters
        }),
        initialCall: true,
        dependencies: [
            companyId,
            subCompanyId,
            selectedTransactionType,
            selectedSubType,
            selectedHasWalletTransaction,
            selectedDateRange,
            isDateRangeChecked,
            workType,
            searchText,
            isPeriodChecked,
            selectedPeriod,
            activeTab,
            selectedInvoiceStatus,
            selectedProviderCompany,
            selectedCreditMode
        ]
    });

    const closeModals = () => {
        setFileUpdateVisibility(false);
    };

    const handleObjection = (orderId, deliveryId, subcompanyId) => {
        setOrderId(orderId);
        setDeliveryId(deliveryId);
        setFileUpdateVisibility(true);
        setSubcompanyIdForObjection(subcompanyId);
    };

    const handleObjectionModalClose = isSubmitted => {
        closeModals();
        if (isSubmitted) {
            setRefreshTrigger(prev => prev + 1);
        }
    };

    useEffect(() => {
        refetch();
    }, [refreshTrigger]);

    //trigger when users all sub-companies are loaded
    useEffect(() => {
        if (stores.companyStore.subCompanyList?.length === 1) {
            setSelectedSubCompanyId(stores.companyStore.subCompanyList[0]._id);
        }
    }, [JSON.stringify(stores.companyStore.subCompanyList)]);

    return useObserver(() => (
        <CreditLogContext.Provider value={creditLogContext}>
            <div style={{width: "100%"}}>
                <div>
                    <VFlex vertical>
                        <CreditLogFilters
                            setSelectedTransactionType={setSelectedTransactionType}
                            setSelectedSubType={setSelectedSubType}
                            setSelectedHasWalletTransaction={setSelectedHasWalletTransaction}
                            setSelectedDateRange={setSelectedDateRange}
                            setIsDateRangeChecked={setIsDateRangeChecked}
                            setSelectedCompanyId={setSelectedCompanyId}
                            setSelectedSubCompanyId={setSelectedSubCompanyId}
                            selectedTransactionType={selectedTransactionType}
                            selectedSubType={selectedSubType}
                            selectedHasWalletTransaction={selectedHasWalletTransaction}
                            selectedDateRange={selectedDateRange}
                            isDateRangeChecked={isDateRangeChecked}
                            selectedPeriod={selectedPeriod}
                            setSelectedPeriod={handlePeriodChange}
                            isPeriodChecked={isPeriodChecked}
                            setIsPeriodChecked={setIsPeriodChecked}
                            companyId={companyId}
                            subCompanyId={subCompanyId}
                            workType={workType}
                            setSelectedWorkType={setSelectedWorkType}
                            selectedInvoiceStatus={selectedInvoiceStatus}
                            setSelectedInvoiceStatus={setSelectedInvoiceStatus}
                            selectedProviderCompany={selectedProviderCompany}
                            setSelectedProviderCompany={setSelectedProviderCompany}
                            selectedCreditMode={selectedCreditMode}
                            setSelectedCreditMode={setSelectedCreditMode}
                        />
                        <ActionButtons
                            loadingExcelDataV2={loadingExcelDataV2}
                            handleDownloadExcelV2={handleDownloadExcelV2}
                            loadingExcelData={loadingExcelData}
                            handleDownloadExcel={handleDownloadExcel}
                            onSearch={setSearchText}
                        />
                    </VFlex>
                    <Tabs activeKey={activeTab} onChange={handleTabChange} destroyInactiveTabPane>
                        <Tabs.TabPane tab="Kredi Hareketleri" key="creditLogTable">
                            <CreditLogTableTab
                                listQueryKey={listQueryKey}
                                paymentTransactionLogs={paymentTransactionLogs}
                                paymentTransactionLogsLoading={paymentTransactionLogsLoading}
                                subCompanyId={subCompanyId}
                                selectedTransactionType={selectedTransactionType}
                                selectedSubType={selectedSubType}
                                handleObjection={handleObjection}
                                pageParam={pageParam}
                                setPageParam={setPageParam}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane tab="Kurye Toplam Detay" key="courierTotalDetail">
                            <CourierDetailTotal
                                paymentTransactionsLogCalculation={
                                    paymentTransactionsLogCalculation
                                }
                                paymentTransactionsLogCalculationLoading={
                                    paymentTransactionsLogCalculationLoading
                                }
                                pageParam={pageParam}
                                setPageParam={setPageParam}
                                tableData={paymentTransactionLogs}
                                sharedLogFilters={sharedLogFilters}
                                fetchDetail={stores.companyStore.paymentTransactionLogDetails.bind(
                                    stores.companyStore
                                )}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane tab="Alt Firma Toplam Detay" key="subCompanyTotalDetail">
                            <SubcompanyDetailTotal
                                subCompanyId={subCompanyId}
                                paymentTransactionsLogCalculation={
                                    paymentTransactionsLogCalculation
                                }
                                paymentTransactionsLogCalculationLoading={
                                    paymentTransactionsLogCalculationLoading
                                }
                                pageParam={pageParam}
                                setPageParam={setPageParam}
                                tableData={paymentTransactionLogs}
                                tableLoading={paymentTransactionLogsLoading}
                                sharedLogFilters={sharedLogFilters}
                                fetchDetail={stores.companyStore.paymentTransactionLogDetails.bind(
                                    stores.companyStore
                                )}
                            />
                        </Tabs.TabPane>
                    </Tabs>
                </div>
                <ObjectionModal
                    subCompanyId={subcompanyIdForObjection}
                    deliveryId={deliveryId}
                    orderId={orderId}
                    visibility={updateFileUpdateVisibilty}
                    onCancel={handleObjectionModalClose}
                />
            </div>
        </CreditLogContext.Provider>
    ));
};
